import { useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, BookOpen, Gift, MessageSquareHeart, QrCode, Receipt } from 'lucide-react';
import { detectClientPlatform } from '@/shared/lib/device';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import type { Passbook } from '@/shared/types';
import type { PublicInvitePassbook } from '@/features/public-invite/api/use-public-invite';
import type { MgmParticipantView } from '@/features/member-get-member/model/types';
import type { ParticipantMe } from '../../api/participant-api';
import {
  participantKeys,
  useCanClaimCoupon,
  useParticipantCoupons,
  useParticipantHistory,
  useParticipantSurvey,
} from '../../api/use-participant';
import { splitCoupons } from '../../model/coupons';
import { hasRulesOrTerms } from '../../model/form-plan';
import { buildHistory, formatBalance } from '../../model/history';
import { ActiveCouponCard, ClaimCouponCard, EmptyCoupons } from '../coupon-card';
import { HistoryEntryRow } from '../history-entry-row';
import { PromoHeroCard } from '../promo-hero-card';
import { TierStatusCard } from '../tier-status-card';
import { WalletCardActions } from '../wallet-card-actions';
import { WalletCardPreview } from '../wallet-card-preview';

/** Abas para onde o Início leva (as mesmas da página). */
export type HomeDestination = 'historico' | 'cartao' | 'cupons' | 'indique' | 'pesquisa' | 'regras';

interface HomeTabProps {
  idPassbook: string;
  passbook: PublicInvitePassbook;
  me: ParticipantMe;
  program: Passbook;
  accentColor?: string;
  foregroundColor?: string;
  onNavigate: (to: HomeDestination) => void;
}

function SectionHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {action}
    </div>
  );
}

/** Cupom ativo com o MESMO card da página de cupons (ou o mesmo estado vazio). */
function CouponsSection({
  idPassbook,
  accentColor,
  foregroundColor,
  onOpen,
}: {
  idPassbook: string;
  accentColor?: string;
  foregroundColor?: string;
  onOpen: () => void;
}) {
  const { data, isPending } = useParticipantCoupons(idPassbook);
  const canClaim = useCanClaimCoupon(idPassbook).data === true;
  const active = data ? splitCoupons(data).active : [];
  return (
    <section className="space-y-3">
      <SectionHeader
        title="Seus cupons"
        action={
          <button
            type="button"
            onClick={onOpen}
            className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {active.length > 1 ? `Ver todos (${active.length})` : 'Ver cupons'}
            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        }
      />
      {isPending ? (
        <Skeleton className="h-56 w-full rounded-2xl" />
      ) : canClaim ? (
        <ClaimCouponCard idPassbook={idPassbook} accentColor={accentColor} foregroundColor={foregroundColor} />
      ) : active[0] ? (
        <ActiveCouponCard coupon={active[0]} accentColor={accentColor} foregroundColor={foregroundColor} />
      ) : (
        <EmptyCoupons />
      )}
    </section>
  );
}

/**
 * Início da área logada: o cartão em destaque (com o botão da carteira do
 * aparelho), atalhos para o que gera valor (cupom, pesquisa, indicação), as
 * últimas movimentações e as regras. Os dados vêm do cache pré-carregado no
 * login, então abre sem skeleton na maior parte das vezes.
 */
export function HomeTab({ idPassbook, passbook, me, program, accentColor, foregroundColor, onNavigate }: HomeTabProps) {
  const platform = useMemo(() => detectClientPlatform(), []);
  const history = useParticipantHistory(idPassbook);
  const survey = useParticipantSurvey(idPassbook, me.features.surveyId ?? '');
  const recent = useMemo(
    () => (history.data ? buildHistory(history.data, program).slice(0, 3) : []),
    [history.data, program],
  );
  const queryClient = useQueryClient();
  // Título/subtítulo da campanha só se a pessoa já abriu o Indique e ganhe (a 1ª consulta cria o código no MGM).
  const mgmCached = queryClient.getQueryData<MgmParticipantView>(participantKeys.mgm(idPassbook));
  const mgmRules = mgmCached?.status === 'ok' ? mgmCached.rules : undefined;

  // Destaques (mesmo card do topo das telas deles); ímpar = o último ocupa a linha inteira no desktop.
  const promos: Array<(className?: string) => ReactNode> = [];
  if (me.features.surveyId) {
    promos.push((className) => (
      <PromoHeroCard
        key="pesquisa"
        icon={MessageSquareHeart}
        iconMotion="pulse"
        eyebrow="Pesquisa de satisfação"
        title={survey.data?.title ?? 'Conte como foi sua experiência'}
        cta="Responder agora"
        onClick={() => onNavigate('pesquisa')}
        accentColor={accentColor}
        foregroundColor={foregroundColor}
        className={className}
      >
        Leva cerca de 1 minuto e ajuda a loja a melhorar.
      </PromoHeroCard>
    ));
  }
  if (me.features.mgm) {
    promos.push((className) => (
      <PromoHeroCard
        key="indique"
        icon={Gift}
        eyebrow="Indique e ganhe"
        title={mgmRules?.title || 'Convide amigos e ganhe'}
        cta="Convidar amigos"
        onClick={() => onNavigate('indique')}
        accentColor={accentColor}
        foregroundColor={foregroundColor}
        className={className}
      >
        {mgmRules?.subtitle || 'Compartilhe seu link e acompanhe suas recompensas.'}
      </PromoHeroCard>
    ));
  }

  return (
    <div className="mx-auto w-full max-w-xl space-y-6">
      <section className="space-y-4">
        <WalletCardPreview
          passbook={passbook}
          holderName={me.profile.name}
          balance={formatBalance(me.balance, program)}
          serialNumber={me.serialNumber}
          tier={me.tier}
        />
        {me.tier ? <TierStatusCard tier={me.tier} program={program} /> : null}
        {me.serialNumber ? (
          platform === 'desktop' ? (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => onNavigate('cartao')}>
                <QrCode className="size-4" />
                Colocar o cartão no celular
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <WalletCardActions
                passbook={passbook}
                serialNumber={me.serialNumber}
                accentColor={accentColor}
                foregroundColor={foregroundColor}
              />
            </div>
          )
        ) : null}
      </section>

      {promos.length > 0 ? (
        <section className="grid gap-4 sm:grid-cols-2">
          {promos.map((render, i) =>
            render(promos.length % 2 === 1 && i === promos.length - 1 ? 'sm:col-span-2' : undefined),
          )}
        </section>
      ) : null}

      {me.features.coupons ? (
        <CouponsSection
          idPassbook={idPassbook}
          accentColor={accentColor}
          foregroundColor={foregroundColor}
          onOpen={() => onNavigate('cupons')}
        />
      ) : null}

      <section className="space-y-3">
        <SectionHeader
          title="Últimas movimentações"
          action={
            recent.length > 0 ? (
              <button
                type="button"
                onClick={() => onNavigate('historico')}
                className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Ver tudo
                <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </button>
            ) : null
          }
        />
        <Card className="px-5 py-2">
          {history.isPending ? (
            <div className="space-y-3 py-3">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))}
            </div>
          ) : recent.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {recent.map((entry) => (
                <HistoryEntryRow key={entry.id} entry={entry} />
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-3 py-4">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-muted">
                <Receipt className="size-4 text-muted-foreground" />
              </span>
              <p className="text-sm text-muted-foreground">
                Suas compras aparecem aqui. Informe seu CPF no caixa para pontuar.
              </p>
            </div>
          )}
        </Card>
      </section>

      {hasRulesOrTerms(passbook) ? (
        <button
          type="button"
          onClick={() => onNavigate('regras')}
          className="group flex w-full items-center gap-3 rounded-2xl border border-border/70 px-5 py-4 text-left transition-colors hover:bg-foreground/[0.03]"
        >
          <BookOpen className="size-5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">Regras do programa e termos</span>
            <span className="block text-xs text-muted-foreground">Como pontuar, resgatar e como usamos seus dados</span>
          </span>
          <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </button>
      ) : null}
    </div>
  );
}
